import Text from '@gym-atoms/Text/Text';
import Button, { ButtonSizes, ButtonVariants } from '@gym-atoms/Button/Button';
import Input from '@gym-atoms/Input/Input';
import {
  AccessCategoryMappingFormInputs,
  GroupedAccessCategory,
  GymAccessCategoryType
} from '@gym-particles/types/models';
import { useTranslation } from 'react-i18next';
import Dropdown, { DropdownChangeEvent, SelectItem } from '@gym-atoms/Dropdown/Dropdown';
import { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useHistory, useLocation, useParams } from 'react-router';
import { API } from 'aws-amplify';
import { getAllMembershipsForAccessCategories } from '@gym-graphql/queries';
import {
  AccessCategoryMembershipInfo,
  AddSiteLocationAccessCategoryInput,
  GymChainAccessCategory,
  GymChainAccessCategoryInput,
  LambdaResponse,
  SiteLocationAccessCategorySite,
  updateAccessCategoryGroupIdInput
} from '@gym-src/API';
import { deleteAccessCategory } from '@gym-redux/slices/accessCategorySlice';
import { useAppDispatch, useAppSelector } from '@gym-redux/store';
import DialogBox, { DialogBoxVariants } from '@gym-atoms/Dialog/DialogBox';
import { getGymChainById } from '@gym-redux/slices/gymChainSlice';
import {
  addAccessCategory,
  addGymChainAccessCategory,
  modifyGymChainAccessCategory,
  updateAccessCategoryGroupId
} from '@gym-graphql/mutations';
import Alert, { AlertType, createAlert } from '../../atoms/Alert/Alert';
import Loader from '../../atoms/Loader/Loader';
import Checkbox from '../../atoms/Checkbox/Checkbox';

const toUniqueNumbers = (values: Array<number | string | null | undefined>): number[] => {
  return Array.from(
    new Set(values.map((value) => Number(value)).filter((value) => Number.isFinite(value)))
  );
};

type MembershipSelectItem = SelectItem & {
  scheduleType: string;
};

const toUniqueMembershipItems = (
  membershipItems: AccessCategoryMembershipInfo[]
): MembershipSelectItem[] => {
  return Array.from(
    new Map(
      membershipItems
        .filter((data) => data && data.membershipId != null)
        .map((data) => ({
          label: `${data.membershipName} (${data.importedId}) • ${data.program} [${data.scheduleType}]`,
          value: data.membershipId,
          scheduleType: data.scheduleType || ''
        }))
        .map((item) => [String(item.value), item])
    ).values()
  );
};

const toScheduleTypeItems = (membershipItems: MembershipSelectItem[]): SelectItem[] => {
  return Array.from(
    new Set(membershipItems.map((item) => item.scheduleType).filter((scheduleType) => scheduleType))
  )
    .sort()
    .map((scheduleType) => ({
      label: scheduleType,
      value: scheduleType
    }));
};

const filterMembershipItemsByScheduleTypes = (
  membershipItems: MembershipSelectItem[],
  scheduleTypes: string[]
): MembershipSelectItem[] => {
  if (scheduleTypes.length === 0) {
    return membershipItems;
  }

  return membershipItems.filter((item) => scheduleTypes.includes(item.scheduleType));
};

const includeSelectedMembershipItems = (
  membershipItems: MembershipSelectItem[],
  selectedMembershipItems: MembershipSelectItem[],
  selectedMembershipIds: number[]
): MembershipSelectItem[] => {
  const selectedIds = new Set(selectedMembershipIds.map((id) => String(id)));
  const selectedItems = selectedMembershipItems.filter((item) =>
    selectedIds.has(String(item.value))
  );

  return Array.from(
    new Map(
      [...membershipItems, ...selectedItems].map((item) => [String(item.value), item])
    ).values()
  );
};

type SiteAccessCategoryChange = {
  field: string;
  previous: string;
  next: string;
};

const formatFieldValue = (value: string | number | undefined): string => {
  if (value === undefined || value === '') {
    return '-';
  }

  return String(value);
};

const getSiteAccessCategoryChanges = (
  currentValues: AccessCategoryMappingFormInputs,
  defaultValues: AccessCategoryMappingFormInputs
): SiteAccessCategoryChange[] => {
  const fields: Array<{
    field: keyof Pick<AccessCategoryMappingFormInputs, 'name' | 'gender' | 'age' | 'status'>;
    label: string;
  }> = [
    { field: 'name', label: 'Name' },
    { field: 'gender', label: 'Gender' },
    { field: 'age', label: 'Age' },
    { field: 'status', label: 'Status' }
  ];

  return fields
    .map(({ field, label }) => ({
      field: label,
      previous: formatFieldValue(defaultValues[field]),
      next: formatFieldValue(currentValues[field])
    }))
    .filter((change) => change.previous !== change.next);
};

const GymAccessCategoryMappingTemplate = (props: GymAccessCategoryMappingTemplateProps) => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation<{ from?: string }>();
  const fromPage = location.state?.from;

  const getBackPath = () => {
    if (fromPage === 'chain') {
      return `/gymChains/${chainId}/gymChainAccessCategories`;
    }
    return `/gymChains/${chainId}/gyms/${gymId}/gymAccessCategories`;
  };
  const { chainId, gymId } = useParams<{ chainId: string; gymId: string }>();
  const [isInserted, setIsInserted] = useState(false);
  const [failedDialogVisible, setFailedDialogVisible] = useState(false);
  const [confirmDialogVisible, setConfirmDialogVisible] = useState(false);
  const [pendingSubmitData, setPendingSubmitData] = useState<AccessCategoryMappingFormInputs>();
  const userRole = useAppSelector((state) => state.user.userRole);
  const [schedulesOnly, setSchedulesOnly] = useState(false);
  const [filterMemberships, setFilterMemberships] = useState(false);
  const alertRef = useRef<AlertType>(null);

  const dispatch = useAppDispatch();
  const templateTypeChangeHandler = useCallback(
    (state) => {
      if (props.templateType) {
        props.templateType(state);
      }
    },
    [props.templateType]
  );
  const userId = useAppSelector((state) => state.user.userId);

  useEffect(() => {
    dispatch(getGymChainById(+chainId));
  }, [chainId]);

  const getTranslationKey = (e: string): string => {
    return props.type === 'new'
      ? 'ADD_NEW_GYM_ACCESS_CATEGORY.' + e
      : 'MODIFY_GYM_ACCESS_CATEGORY.' + e;
  };

  const accessCategoryData = props.accessCategoryMapping
    ? (Object.values(
        props.accessCategoryMapping.reduce((acc, current) => {
          const {
            accessCategoryId,
            arxAccessCategoryId,
            siteId,
            id,
            name,
            gender,
            age,
            status,
            ...siteLocationDetails
          } = current;

          if (!acc[accessCategoryId]) {
            acc[accessCategoryId] = {
              accessCategoryId,
              arxAccessCategoryId,
              siteId,
              id,
              name,
              gender,
              age,
              status,
              siteLocations: []
            };
          }

          const hasSiteLocation = acc[accessCategoryId].siteLocations.some(
            (siteLocation) =>
              siteLocation.siteLocationAccessCategoryId ===
              siteLocationDetails.siteLocationAccessCategoryId
          );

          if (!hasSiteLocation) {
            acc[accessCategoryId].siteLocations.push(siteLocationDetails);
          }

          return acc;
        }, {})
      ) as GroupedAccessCategory[])
    : [];

  const accessCategoryDefaultValues: AccessCategoryMappingFormInputs = {
    name: accessCategoryData[0]?.name || '',
    gender: accessCategoryData[0]?.gender || '',
    age: accessCategoryData[0]?.age || 0,
    status: accessCategoryData[0]?.status || '',
    arxCategoryId: accessCategoryData[0]?.arxAccessCategoryId || '',
    accessCategoryId: accessCategoryData[0]?.accessCategoryId || -1,
    membershipIds: toUniqueNumbers(
      accessCategoryData[0]?.siteLocations.map(
        (location: SiteLocationAccessCategorySite) => location.membershipId
      ) || []
    )
  };

  const [formValues, setFormValues] = useState(accessCategoryDefaultValues);
  const [allMembershipsDropDownItems, setAllMembershipsDropDownItems] = useState<
    Array<MembershipSelectItem>
  >([]);
  const [membershipsDropDownItems, setMembershipsDropDownItems] = useState<
    Array<MembershipSelectItem>
  >([]);
  const [scheduleTypesDropDownItems, setScheduleTypesDropDownItems] = useState<Array<SelectItem>>(
    []
  );
  const [selectedScheduleTypes, setSelectedScheduleTypes] = useState<string[]>([]);
  const [isMembershipLoading, setIsMembershipLoading] = useState(false);
  const [selectedMembershipsItems, setSelectedMembershipsItems] = useState<number[]>([]);
  const isAccessCategoriesLoading = useAppSelector(
    (state) => state.accessCategory.isAccessCategoriesLoading
  );
  const [defaultSelectedMemberships, setDefaultSelectedMemberships] = useState<
    Array<SiteLocationAccessCategorySite>
  >([]);
  const {
    register,
    handleSubmit,
    setValue,
    errors,
    reset,
    getValues
  } = useForm<AccessCategoryMappingFormInputs>({
    defaultValues: accessCategoryDefaultValues
  });

  const emptyValues: AccessCategoryMappingFormInputs = {
    name: '',
    gender: '',
    age: 0,
    status: '',
    membershipIds: [],
    accessCategoryId: -1,
    arxCategoryId: ''
  };

  const genderDropDownItems = [
    {
      label: t(getTranslationKey('FORM_GENDER_DROPDOWN_MALE')),
      value: 'male'
    },
    {
      label: t(getTranslationKey('FORM_GENDER_DROPDOWN_FEMALE')),
      value: 'female'
    },
    {
      label: t(getTranslationKey('FORM_GENDER_DROPDOWN_NA')),
      value: 'n/a'
    }
  ];

  const statusDropDownItems = [
    {
      label: t(getTranslationKey('FORM_STATUS_DROPDOWN_ACTIVE')),
      value: 'active'
    },
    { label: t(getTranslationKey('FORM_STATUS_DROPDOWN_INACTIVE')), value: 'inactive' }
  ];

  const inputFieldChangeHandler = (e: FormEvent<HTMLInputElement>) => {
    const fieldName = e.currentTarget.name as keyof AccessCategoryMappingFormInputs;
    const fieldValue = fieldName === 'age' ? Number(e.currentTarget.value) : e.currentTarget.value;

    setValue(fieldName, fieldValue);
    setFormValues({ ...formValues, [fieldName]: fieldValue });
  };

  const dropdownChangeHandler = (e: DropdownChangeEvent) => {
    setValue(e.target.name as keyof AccessCategoryMappingFormInputs, e.value);
    setFormValues({ ...formValues, [e.target.name]: e.value });
  };

  const membershipsDropdownHandler = async (e: DropdownChangeEvent) => {
    const selectedMembershipIds = toUniqueNumbers(e.value || []);
    const removedMemberships = defaultSelectedMemberships
      .map((y: SiteLocationAccessCategorySite) => y.membershipId)
      .filter((membershipId) => !selectedMembershipIds.includes(Number(membershipId)));

    let response;

    if (removedMemberships.length > 0) {
      if (
        selectedMembershipsItems &&
        selectedMembershipsItems?.length > selectedMembershipIds.length
      ) {
        //response = await checkRemovableMemberships(removedMemberships);
        response = true;
      } else {
        response = true;
      }
    } else {
      response = true;
    }
    if (response) {
      setValue('membershipIds', selectedMembershipIds);
      setFormValues({ ...formValues, membershipIds: selectedMembershipIds });
    } else {
      return;
    }
    setSelectedMembershipsItems(selectedMembershipIds);
    console.log({ formValues });
  };

  const scheduleTypesDropdownHandler = (e: DropdownChangeEvent) => {
    setSelectedScheduleTypes((e.value || []).map((value) => String(value)));
  };

  useEffect(() => {
    register('name', {
      required: t(getTranslationKey('FORM_REQUIRED_FIELD_ERROR')) as string
    });
    register('gender');
    register('age');
    register('status', {
      required: t(getTranslationKey('FORM_REQUIRED_FIELD_ERROR')) as string
    });
    register('accessCategoryId', {
      required: t(getTranslationKey('FORM_REQUIRED_FIELD_ERROR')) as string
    });
    register('membershipIds', {
      validate: (value) => {
        if (value === -1) {
          return false;
        } else if (Array.isArray(value) && value.length === 0) {
          return false;
        } else {
          return true;
        }
      }
    });
    register('arxCategoryId');
  }, [register]);

  const getGymMemberships = async () => {
    setIsMembershipLoading(true);

    try {
      const variables = {
        siteLocationId: `(${+gymId})`,
        filterMemberships: filterMemberships,
        accessCategoryId:
          props.type === 'modify' && accessCategoryDefaultValues.accessCategoryId > 0
            ? accessCategoryDefaultValues.accessCategoryId
            : 0
      };

      const response = await (API.graphql({
        query: getAllMembershipsForAccessCategories,
        variables
      }) as Promise<{
        data: { getAllMembershipsForAccessCategories: AccessCategoryMembershipInfo[] };
      }>);

      const membershipsDropDownItems = toUniqueMembershipItems(
        response.data.getAllMembershipsForAccessCategories || []
      );
      const membershipsDropDownItemsWithSelectedMemberships = includeSelectedMembershipItems(
        membershipsDropDownItems,
        allMembershipsDropDownItems,
        formValues.membershipIds || []
      );
      console.log({ membershipsDropDownItems });

      setAllMembershipsDropDownItems(membershipsDropDownItemsWithSelectedMemberships);
      setScheduleTypesDropDownItems(
        toScheduleTypeItems(membershipsDropDownItemsWithSelectedMemberships)
      );
    } catch (error) {
      console.error('Error fetching memberships:', error);
      setFailedDialogVisible(true);
    } finally {
      setIsMembershipLoading(false);
    }
  };

  useEffect(() => {
    const filteredMemberships = filterMembershipItemsByScheduleTypes(
      allMembershipsDropDownItems,
      selectedScheduleTypes
    );

    setMembershipsDropDownItems(
      includeSelectedMembershipItems(
        filteredMemberships,
        allMembershipsDropDownItems,
        formValues.membershipIds || []
      )
    );
  }, [allMembershipsDropDownItems, selectedScheduleTypes, formValues.membershipIds]);

  useEffect(() => {
    const availableScheduleTypes = scheduleTypesDropDownItems.map((item) => String(item.value));
    const availableSelectedScheduleTypes = selectedScheduleTypes.filter((scheduleType) =>
      availableScheduleTypes.includes(scheduleType)
    );

    if (availableSelectedScheduleTypes.length !== selectedScheduleTypes.length) {
      setSelectedScheduleTypes(availableSelectedScheduleTypes);
    }
  }, [scheduleTypesDropDownItems]);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        await getGymMemberships();
      } catch (err) {
        if (isMounted) setFailedDialogVisible(true);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [gymId, filterMemberships]);

  const addAccessCategoryMappingData = async (
    formInputAccessCategory: AccessCategoryMappingFormInputs
  ) => {
    try {
      const siteAccessCategory = await addSiteAccessCategory(formInputAccessCategory);
      await addSiteLocationAccessCategory(formInputAccessCategory, siteAccessCategory);

      createAlert(alertRef, {
        content: t(getTranslationKey('ALERT_MESSAGE_SUCCESS')),
        severity: 'success'
      });

      setTimeout(() => history.push(getBackPath()), 3000);
    } catch (error) {
      setFailedDialogVisible(true);
      console.error('Add Access Category Error:', error);
      throw error;
    }
  };

  const getSiteAccessCategoryInput = (
    data: AccessCategoryMappingFormInputs,
    id?: number
  ): GymChainAccessCategoryInput => {
    return {
      id,
      siteId: +chainId || 0,
      name: data.name || '',
      gender: data.gender || '',
      age: +data.age || 0,
      schedulesOnly: schedulesOnly,
      status: data.status || '',
      createdBy: userId,
      lastModifiedBy: userId
    };
  };

  const addSiteAccessCategory = async (
    data: AccessCategoryMappingFormInputs
  ): Promise<GymChainAccessCategory> => {
    const response = await (API.graphql({
      query: addGymChainAccessCategory,
      variables: {
        input: getSiteAccessCategoryInput(data)
      }
    }) as Promise<{ data: { addGymChainAccessCategory: GymChainAccessCategory } }>);

    return response.data.addGymChainAccessCategory;
  };

  const modifySiteAccessCategory = async (
    data: AccessCategoryMappingFormInputs
  ): Promise<GymChainAccessCategory> => {
    const response = await (API.graphql({
      query: modifyGymChainAccessCategory,
      variables: {
        input: getSiteAccessCategoryInput(data, data.accessCategoryId)
      }
    }) as Promise<{ data: { modifyGymChainAccessCategory: GymChainAccessCategory } }>);

    return response.data.modifyGymChainAccessCategory;
  };

  const modifySiteLocationAccessCategory = async (
    modifySiteLocationAccessCategoryInput: updateAccessCategoryGroupIdInput
  ) => {
    console.log({ modifySiteLocationAccessCategoryInput });
    try {
      await API.graphql({
        query: updateAccessCategoryGroupId,
        variables: {
          input: modifySiteLocationAccessCategoryInput
        }
      });
    } catch (error) {
      setFailedDialogVisible(true);
      console.error(error);
      throw error;
    }
  };

  const addSiteLocationAccessCategory = async (
    data: AccessCategoryMappingFormInputs,
    siteAccessCategory: GymChainAccessCategory
  ) => {
    try {
      const siteAccessCategoryId = Number(siteAccessCategory.id || data.accessCategoryId);

      if (!Number.isFinite(siteAccessCategoryId) || siteAccessCategoryId <= 0) {
        throw new Error('Cannot add location access category without a site access category id');
      }

      const addSiteLocationAccessCategoryInput: AddSiteLocationAccessCategoryInput = {
        name: (siteAccessCategory.name || data.name || '').replace(/'/g, "''"),
        membershipIds: Array.isArray(data.membershipIds)
          ? data.membershipIds.map((id) => +id)
          : [data.membershipIds],
        siteLocationIds: [+gymId],
        gender: siteAccessCategory.gender || data.gender,
        age: siteAccessCategory.age || +data.age || 0,
        accessCategoryId: siteAccessCategoryId,
        arxAccessCategoryId: data.arxCategoryId,
        status: siteAccessCategory.status || data.status,
        createdBy: userId,
        lastModifiedBy: userId,
        schedulesOnly: schedulesOnly,
        siteLocationMemberships: []
      };

      console.log({ addSiteLocationAccessCategoryInput });

      const result = await (API.graphql({
        query: addAccessCategory,
        variables: {
          input: addSiteLocationAccessCategoryInput
        }
      }) as Promise<{
        data: { addAccessCategory: LambdaResponse };
      }>);
      if (result.data.addAccessCategory?.error) {
        throw result.data.addAccessCategory;
      }
    } catch (error) {
      setFailedDialogVisible(true);
      console.error(error);
      throw error;
    }
  };

  const removeLocationAccessCategory = async (accessCategoryId: number | null | undefined) => {
    try {
      const deleteAccessCategoryId = Number(accessCategoryId);

      if (!Number.isFinite(deleteAccessCategoryId)) {
        setFailedDialogVisible(true);
        console.error('Cannot remove location access category without an id', { accessCategoryId });
        return;
      }

      await dispatch(
        deleteAccessCategory({
          ids: [deleteAccessCategoryId],
          siteLocationId: +gymId
        })
      );
    } catch (error) {
      setFailedDialogVisible(true);
      console.error(error);
    }
  };

  const modifyAccessCategoryMappingData = async (
    formInputAccessCategory: AccessCategoryMappingFormInputs
  ) => {
    try {
      const tasks: Promise<void>[] = [];
      const siteAccessCategoryChanges = getSiteAccessCategoryChanges(
        formInputAccessCategory,
        accessCategoryDefaultValues
      );
      const currentSiteAccessCategory: GymChainAccessCategory = {
        __typename: 'GymChainAccessCategory',
        id: accessCategoryData[0]?.accessCategoryId,
        siteId: accessCategoryData[0]?.siteId,
        name: accessCategoryData[0]?.name,
        gender: accessCategoryData[0]?.gender,
        age: accessCategoryData[0]?.age,
        status: accessCategoryData[0]?.status,
        schedulesOnly: schedulesOnly
      };
      const siteAccessCategory =
        siteAccessCategoryChanges.length > 0
          ? await modifySiteAccessCategory(formInputAccessCategory)
          : currentSiteAccessCategory;
      const defaultMembershipIds = accessCategoryDefaultValues.membershipIds.map((membership) =>
        Number(membership)
      );
      const addedMemberships =
        formInputAccessCategory.membershipIds?.filter(
          (id) => !defaultMembershipIds.includes(+id)
        ) || [];

      if (addedMemberships.length > 0) {
        tasks.push(addSiteLocationAccessCategory(formInputAccessCategory, siteAccessCategory));
      }

      const removedMemberships = defaultMembershipIds.filter(
        (id) => !formInputAccessCategory.membershipIds?.includes(id)
      );
      console.log({ defaultMembershipIds }, { addedMemberships }, { removedMemberships });

      removedMemberships.forEach((id) => {
        const membershipEntry = accessCategoryData[0].siteLocations.filter(
          (location) => location.membershipId === id
        )[0];
        tasks.push(removeLocationAccessCategory(membershipEntry?.siteLocationAccessCategoryId));
      });

      if (formInputAccessCategory.arxCategoryId !== accessCategoryDefaultValues.arxCategoryId) {
        tasks.push(
          modifySiteLocationAccessCategory({
            membershipIds: `(${formInputAccessCategory.membershipIds.join(', ')})`,
            siteLocationIds: `(${+gymId})`,
            arxAccessCategoryId: formInputAccessCategory.arxCategoryId,
            accessCategoryId: siteAccessCategory.id || formInputAccessCategory.accessCategoryId,
            lastModifiedBy: userId
          })
        );
      }

      await Promise.all(tasks);
      createAlert(alertRef, {
        content: t(getTranslationKey('ALERT_MESSAGE_SUCCESS')),
        severity: 'success'
      });

      setTimeout(() => history.push(getBackPath()), 3000);
    } catch (error) {
      setFailedDialogVisible(true);
      console.error(error);
    }
  };

  const onSubmitHandler = async (data: AccessCategoryMappingFormInputs) => {
    setIsInserted(true);
    try {
      if (props.type === 'new') {
        await addAccessCategoryMappingData(data);
      } else {
        const siteAccessCategoryChanges = getSiteAccessCategoryChanges(
          data,
          accessCategoryDefaultValues
        );

        if (siteAccessCategoryChanges.length > 0) {
          setPendingSubmitData(data);
          setConfirmDialogVisible(true);
          return;
        }

        await modifyAccessCategoryMappingData(data);
        templateTypeChangeHandler(false);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsInserted(false);
    }
  };

  const confirmSiteAccessCategoryChanges = async () => {
    if (!pendingSubmitData) {
      setConfirmDialogVisible(false);
      return;
    }

    try {
      setIsInserted(true);
      setConfirmDialogVisible(false);
      await modifyAccessCategoryMappingData(pendingSubmitData);
      templateTypeChangeHandler(false);
      setPendingSubmitData(undefined);
    } finally {
      setIsInserted(false);
    }
  };

  const ActionFailedDialog = () => {
    return (
      <div>
        <DialogBox
          variant={DialogBoxVariants.basic}
          dialogVisible={failedDialogVisible}
          onHideCallback={() => setFailedDialogVisible(false)}
          dialogHeader={t(getTranslationKey('FORM_FAILURE_DIALOG_HEADER'))}
          dialogFooter={
            <Button
              label={t(getTranslationKey('FORM_FAILURE_DIALOG_BUTTON'))}
              onClick={() => setFailedDialogVisible(false)}
            />
          }
          dialogDismissableMask={true}
          dialogClosable={false}
          content={<Text>{t(getTranslationKey('FORM_FAILURE_DIALOG_BODY'))}</Text>}
        />
      </div>
    );
  };

  const ConfirmSiteAccessCategoryChangesDialog = () => {
    const changes = pendingSubmitData
      ? getSiteAccessCategoryChanges(pendingSubmitData, accessCategoryDefaultValues)
      : [];

    return (
      <DialogBox
        variant={DialogBoxVariants.long}
        dialogVisible={confirmDialogVisible}
        onHideCallback={() => setConfirmDialogVisible(false)}
        dialogHeader={t(getTranslationKey('CONFIRM_SITE_ACCESS_CATEGORY_DIALOG_HEADER'))}
        dialogFooter={
          <div>
            <Button
              label={t(getTranslationKey('CONFIRM_SITE_ACCESS_CATEGORY_DIALOG_CANCEL'))}
              variant={ButtonVariants.textonly}
              onClick={() => {
                setConfirmDialogVisible(false);
                setPendingSubmitData(undefined);
              }}
            />
            <Button
              label={t(getTranslationKey('CONFIRM_SITE_ACCESS_CATEGORY_DIALOG_CONFIRM'))}
              onClick={confirmSiteAccessCategoryChanges}
              icon={isInserted ? 'pi-spinner pi-spin' : ''}
              disabled={isInserted}
            />
          </div>
        }
        dialogDismissableMask={true}
        dialogClosable={false}
        content={
          <div>
            <Text>{t(getTranslationKey('CONFIRM_SITE_ACCESS_CATEGORY_DIALOG_BODY'))}</Text>
            <ul>
              {changes.map((change) => (
                <li key={change.field}>
                  <span className="fw-bold">{change.field}:</span> {change.previous} {' -> '}
                  {change.next}
                </li>
              ))}
            </ul>
          </div>
        }
      />
    );
  };

  const loaderElement = () => {
    return (
      <div className="loaderHeader">
        <div className="loaderContent">
          <Loader shape="rectangle" width="quarter" />
        </div>
        <div>
          <Loader shape="rectangle" width="full" />
        </div>
      </div>
    );
  };

  return (
    <div>
      {isAccessCategoriesLoading ? (
        [...Array(5)].map((element, index) => {
          return <div key={index}> {loaderElement()} </div>;
        })
      ) : (
        <div>
          <div className="d-flex flex-row">
            <div className="col">
              <Alert refProp={alertRef} />
            </div>
          </div>
          <form className="input-mb20" onSubmit={handleSubmit(onSubmitHandler)} noValidate>
            <div className="col-12 mb25">
              <Input
                label={t(getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_NAME_LABEL'))}
                variant="basic"
                name="name"
                value={formValues.name}
                onChange={inputFieldChangeHandler}
                error={!!errors.name}
                errorMessage={errors.name?.message}
                placeholder={t(getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_NAME_PLACEHOLDER'))}
                required={true}
              />
            </div>

            <div className="col-12 mb25">
              <label className="fw-bold">
                {t(getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_GENDER_LABEL'))}
              </label>
              <Dropdown
                key={'gender-selection'}
                name="gender"
                options={genderDropDownItems}
                onChange={dropdownChangeHandler}
                value={formValues.gender}
                placeholder={t(getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_GENDER_PLACEHOLDER'))}
                error={!!errors.gender}
              />
            </div>

            <div className="col-12 mb25">
              <Input
                label={t(getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_AGE_LABEL'))}
                variant="basic"
                name="age"
                value={formValues.age > 0 ? formValues.age.toString() : ''}
                onChange={inputFieldChangeHandler}
                error={!!errors.age}
                errorMessage={errors.age?.message}
                placeholder={t(getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_AGE_PLACEHOLDER'))}
                required={false}
              />
            </div>

            <div className="col-12 mb25">
              <label className="fw-bold">
                {t(getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_STATUS_LABEL'))}
                <span className="p-error"> *</span>
              </label>
              <Dropdown
                key={'status-selection'}
                name="status"
                options={statusDropDownItems}
                value={formValues.status}
                onChange={dropdownChangeHandler}
                placeholder={t(getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_STATUS_PLACEHOLDER'))}
                error={!!errors.status}
              />
            </div>

            <div className="col-12 mb25">
              <Checkbox
                name="filterMemberships"
                label={t(getTranslationKey('FILTER_MEMBERSHIPS_CHECKBOX'))}
                checked={filterMemberships}
                onChange={() => setFilterMemberships(!filterMemberships)}
              />
            </div>

            <div className="col-12 mb25">
              <label className="fw-bold">
                {t(getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_SCHEDULE_TYPE_LABEL'))}
              </label>
              <Dropdown
                chips={true}
                name="scheduleTypes"
                placeholder={t(
                  getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_SCHEDULE_TYPE_PLACEHOLDER')
                )}
                value={selectedScheduleTypes}
                options={scheduleTypesDropDownItems}
                onChange={scheduleTypesDropdownHandler}
                disabled={isMembershipLoading || scheduleTypesDropDownItems.length === 0}
                key={`schedule-types-dropdown-${scheduleTypesDropDownItems.length}`}
              />
            </div>

            <div className="col-12 mb25">
              <label className="fw-bold">
                {t(getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_MEMBERSHIP_LABEL'))}
              </label>
              <Dropdown
                loading={isMembershipLoading}
                disabled={isMembershipLoading}
                key={`membership-dropdown-${membershipsDropDownItems.length}-${selectedScheduleTypes.length}`}
                chips={true}
                name="membershipIds"
                options={membershipsDropDownItems}
                onChange={membershipsDropdownHandler}
                value={formValues.membershipIds || []}
                placeholder={t(
                  getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_MEMBERSHIP_PLACEHOLDER')
                )}
              />
            </div>

            {formValues.membershipIds.length > 0 && (
              <div className="col-12 mb25">
                <h4>Selected Memberships:</h4>
                {!isMembershipLoading &&
                  formValues.membershipIds?.map((id, index) => {
                    const matchingItem = membershipsDropDownItems.find(
                      (obj) => String(obj.value) === String(id)
                    );

                    return (
                      <span
                        key={`${id}-${index}`}
                        className="p-badge"
                        style={{ color: 'white', fontWeight: 'bold', marginRight: '6px' }}>
                        {matchingItem ? matchingItem.label : t('COMMON.LOADING')}
                      </span>
                    );
                  })}
              </div>
            )}
            <div className="col-12 mb25">
              <Input
                label={t(getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_ARX_ID_LABEL'))}
                variant="basic"
                name="arxCategoryId"
                value={formValues.arxCategoryId}
                onChange={inputFieldChangeHandler}
                error={!!errors.arxCategoryId}
                errorMessage={errors.arxCategoryId?.message}
                placeholder={t(getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_ARX_ID_PLACEHOLDER'))}
              />
            </div>

            <div className="d-flex flex-row mt35 btn-min-w-110">
              <div className="mr15">
                <Button
                  icon={isInserted ? 'pi-spinner pi-spin' : ''}
                  label={t(getTranslationKey('FORM_BTN_LABEL_SAVE'))}
                  size={ButtonSizes.medium}
                  disabled={isInserted}
                />
              </div>
              <Button
                label={t(getTranslationKey('FORM_BTN_LABEL_CANCEL'))}
                variant={ButtonVariants.textonly}
                onClick={() => {
                  history.push(getBackPath());
                }}
                disabled={isInserted}
              />
            </div>
            <ActionFailedDialog />
            <ConfirmSiteAccessCategoryChangesDialog />
          </form>
        </div>
      )}
    </div>
  );
};

type GymAccessCategoryMappingTemplateType = 'new' | 'modify';

export interface GymAccessCategoryMappingTemplateProps {
  /** Type of the template: new or modify */
  type: GymAccessCategoryMappingTemplateType;
  /** Heading of the template */
  heading?: string;
  /** form values*/
  accessCategoryMapping?: GymAccessCategoryType[] | undefined;
  /** template change function */
  templateType?: (e: boolean) => void;
}

export default GymAccessCategoryMappingTemplate;
